import { useCallback } from "react";
import { toast } from "react-toastify";
import { createDerivedPromo, updateDerivedPromo } from "../../../api";
import { UAL_SOURCES } from "../../../constants/ualActions";
import {
  mapPromoToCreateDerivedPayload,
  mapPromoToUpdateDerivedPayload,
} from "../../../utils/derivedRatesMapper";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { EMPTY_COMBO_WARNING_MESSAGE } from "../../../constants/promoValidation";
import { logEmptyComboBlocked } from "../../../utils/promoComboValidation";
import {
  resolveSmartPromoType,
  buildSmartPromoSuccessMessage,
} from "../utils/promoSubmitMessages";

/**
 * Pilot-hotel submit branch — derived rates promo API.
 */
export function useSubmitDerivedPromo({
  promo,
  selectedProperty,
  isEdit,
  isDuplicate,
  isMarket,
  calculationType,
  hideModal,
  setLoading,
  // Shared with the standard branch via useCreatePromoSubmit: resolves the
  // combos that belong to one property (EO-1060 cross-property guard).
  getCodesForProperty,
  recordRecommendationUsed,
}) {
  const submitDerivedPromo = useCallback(
    (isDraft = false) => {
      setLoading(true);
      const selectedProperties = promo?.selectedProperties || [];
      const propertyIds =
        selectedProperties.length > 0
          ? selectedProperties.map(Number)
          : [Number(selectedProperty?.propertyId)];
      const derivedMeta = promo?._derivedRateMeta;

      // Mirror non-pilot promoType logic so smart promos
      // (action-center / market campaigns) flow through correctly.
      const enrichedPromo = {
        ...promo,
        promoType: resolveSmartPromoType(promo, isMarket),
      };
      const { suggestionPromoId, promoCategory } = enrichedPromo;
      const isSmartPromo = !!(suggestionPromoId && promoCategory);

      // Shared empty-combo guard for the update and create branches below. A
      // launch must not send an empty rateRoomSelections (a promo left with no
      // roomtype/rateplan combo). Drafts are allowed to be incomplete (matching
      // the standard flow), so they skip this — blocking a "Save as Draft"
      // would be a regression. Returns true when the submit was blocked.
      const isBlockedForEmptyCombos = (payload, action) => {
        if (isDraft) return false;
        const hasCombinations = payload.rateRoomSelections?.some(
          (selection) => selection.combinations?.length > 0
        );
        if (hasCombinations) return false;
        setLoading(false);
        toast.warning(EMPTY_COMBO_WARNING_MESSAGE);
        logEmptyComboBlocked({
          action,
          promoId: derivedMeta?.promoId,
          propertyId: selectedProperty?.propertyId,
        });
        return true;
      };

      // Shared success flow for the update and create branches below — they
      // must stay in lockstep (toast, recommendation bookkeeping, modal close).
      const handleSubmitSuccess = async ({ verb, successMessage }) => {
        if (isSmartPromo) {
          toast.success(
            buildSmartPromoSuccessMessage(promoCategory, "Promotion", verb)
          );
        } else {
          toast.success(successMessage);
        }
        await recordRecommendationUsed(suggestionPromoId, promoCategory);
        hideModal();
        setLoading(false);
      };

      if (isEdit && !isDuplicate && derivedMeta?.promoId) {
        // Update existing promo
        const payload = mapPromoToUpdateDerivedPayload(
          enrichedPromo,
          calculationType
        );

        if (isBlockedForEmptyCombos(payload, "update")) return;

        return updateDerivedPromo(
          derivedMeta.promoId,
          selectedProperty?.propertyId,
          payload,
          UAL_SOURCES.PROMOTIONS_CREATE_FORM
        )
          .then(() =>
            handleSubmitSuccess({
              verb: "updated",
              successMessage: "Promo updated successfully",
            })
          )
          .catch((err) => {
            setLoading(false);
            toast.error(`Failed to update promo: ${getApiErrorMessage(err)}`);
          });
      } else {
        // Create new promo
        // D8: with isMarket=true, useOfferTypeState empties allowedRateRoomCodes.
        // Rebuild all room×rateplan combos when the selection is empty (or this is
        // a single-property action-center smart promo) so the derived create
        // payload is not submitted with an empty rateRoomSelections. Mirrors the
        // non-pilot submit guard (isMarket || !hasMultipleProperties).
        const hasMultipleProperties =
          selectedProperties.length > 1 &&
          promo?.allowedRateRoomCodesByProperty;
        const createPromo = {
          ...enrichedPromo,
          allowedRateRoomCodes:
            isMarket || !hasMultipleProperties
              ? // Single property: keep only the combos that belong to the
                // property this promo is being created for, so a selection
                // left over from another property can't be sent under it.
                getCodesForProperty(promo?.allowedRateRoomCodes, propertyIds[0])
              : promo?.allowedRateRoomCodes,
        };
        const payload = mapPromoToCreateDerivedPayload(
          createPromo,
          propertyIds,
          calculationType,
          isDraft
        );

        if (isBlockedForEmptyCombos(payload, "create")) return;

        return createDerivedPromo(payload, UAL_SOURCES.PROMOTIONS_CREATE_FORM)
          .then(() =>
            handleSubmitSuccess({
              verb: "created",
              successMessage: isDraft
                ? "Promo saved as draft successfully"
                : "Promo created successfully",
            })
          )
          .catch((err) => {
            setLoading(false);
            toast.error(`Failed to save promo: ${getApiErrorMessage(err)}`);
          });
      }
    },
    [
      setLoading,
      promo,
      isEdit,
      isDuplicate,
      isMarket,
      selectedProperty,
      calculationType,
      hideModal,
      recordRecommendationUsed,
      getCodesForProperty,
    ]
  );

  return { submitDerivedPromo };
}
